import { useEffect, useMemo, useRef, useState } from "react"
import type { PageInfo, PageSize } from "./types"

const PAGE_GAP = 20

interface UsePdfLayoutResult {
	pageInfos: PageInfo[]
	totalHeight: number
	baseScale: number
	scrollContainerRef: React.RefObject<HTMLDivElement | null>
}

export const usePdfLayout = (
	totalPages: number,
	originalPageSizes: Map<number, PageSize>
): UsePdfLayoutResult => {
	const scrollContainerRef = useRef<HTMLDivElement>(null)
	const [containerWidth, setContainerWidth] = useState(0)

	// Measure and track container width
	useEffect(() => {
		const el = scrollContainerRef.current
		if (!el) return

		const measure = () => {
			const computed = window.getComputedStyle(el)
			const paddingLeft = Number.parseFloat(computed.paddingLeft || "0") || 0
			const paddingRight = Number.parseFloat(computed.paddingRight || "0") || 0
			const contentWidth = Math.max(
				0,
				el.clientWidth - paddingLeft - paddingRight
			)
			setContainerWidth(prev =>
				Math.abs(prev - contentWidth) > 0.5 ? contentWidth : prev
			)
		}

		measure()

		const resizeObserver = new ResizeObserver(() => measure())
		resizeObserver.observe(el)

		return () => {
			resizeObserver.disconnect()
		}
	}, [])

	const { pageInfos, totalHeight, baseScale } = useMemo(() => {
		if (totalPages === 0 || originalPageSizes.size === 0 || containerWidth <= 0) {
			return { pageInfos: [], totalHeight: 0, baseScale: 1 }
		}

		const firstPage = originalPageSizes.get(1)
		if (!firstPage) return { pageInfos: [], totalHeight: 0, baseScale: 1 }

		const targetPageWidth = Math.min(800, Math.max(240, containerWidth))
		const scale = targetPageWidth / firstPage.width

		if (!Number.isFinite(scale) || scale <= 0) {
			return { pageInfos: [], totalHeight: 0, baseScale: 1 }
		}

		const pages: PageInfo[] = []
		let currentY = 0

		for (let i = 1; i <= totalPages; i++) {
			const size = originalPageSizes.get(i)
			if (!size) continue

			const pageHeight = size.height * scale

			pages.push({
				pageNumber: i,
				height: pageHeight,
				yPosition: currentY,
				originalViewportHeight: size.height,
			})

			currentY += pageHeight + (i < totalPages ? PAGE_GAP : 0)
		}

		return { pageInfos: pages, totalHeight: currentY, baseScale: scale }
	}, [totalPages, originalPageSizes, containerWidth])

	return { pageInfos, totalHeight, baseScale, scrollContainerRef }
}
